import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import Cropper from 'react-easy-crop'
import { getOrientation } from 'get-orientation/browser'
import { Button, Checkbox, Flex, WhiteSpace, WingBlank } from 'antd-mobile'
import ImageBlobReduce from 'image-blob-reduce'
import Pica from 'pica'
import { showToast } from '../../components/common/tools'

import { getCroppedImg, getRotatedImage } from './canvasUtils'
import { dataURLtoBlobOrBlodURL, getParameterByName, showAlert } from '../../components/common/utils'
import { customizedImage, getCustomizeEcardUrl, getOssSignature, uploadSignature } from '../../services/api'
import CardImageShow from '../../components/CertShow/CertShow'

import styles from './MermaidUpload.module.css'
import { pageLoaded, pageLoading } from '../../actions'

import lightLogo from '../../assets/images/logo/padi-light.png'
import blackLogo from '../../assets/images/logo/padi-black.png'
import mixLogo from '../../assets/images/logo/padi-mix.png'

const pica = Pica({ features: ['js', 'wasm', 'cib'] })
new ImageBlobReduce({ pica })
const ORIENTATION_TO_ANGLE = {
  3: 180,
  6: 90,
  8: -90,
}

const logos = [lightLogo, blackLogo, mixLogo]
const UploadButton = ({ upload, ecardName }) => (
  <Flex direction="column" justify="center" className={styles.overlay} onClick={upload}>
    <i className="icon fa fa-camera fa-lg"></i>
    <span>
      <FormattedMessage id="customize-cert-create-pre" />
    </span>
    <span>{ecardName}</span>
    <span>
      <FormattedMessage id="customize-cert-create-suffix" />
    </span>
  </Flex>
)

const UploadHints = () => (
  <ul>
    <li>
      <FormattedMessage id="customize-cert-hint2" />
    </li>
    <li>
      <FormattedMessage id="customize-cert-hint3" />
    </li>
    <li>
      <FormattedMessage id="customize-cert-hint5" />
    </li>
  </ul>
)

const Hints = ({ uploaded }) => {
  return uploaded && uploaded.status === 'CLOSE' ? (
    <>
      <p>
        <FormattedMessage id="customize-cert-upload-rejected" />
      </p>
      <p>{uploaded.note}</p>
    </>
  ) : (
    <UploadHints />
  )
}

const MermaidUpload = () => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const {
    state: { ecard = {} },
  } = useLocation()
  const ecardName = ecard.ecardName
  const ecardId = ecard.encodedId

  const testonly = getParameterByName('testonly')

  const cropContainer = useRef()
  const [imageSrc, setImageSrc] = React.useState(null)
  const [uploaded, setUploaded] = useState(null)
  const [queryStatus, setQueryStatus] = useState(true)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [rotation, setRotation] = useState(0)
  const [zoom, setZoom] = useState(3)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [croppedImage, setCroppedImage] = useState(null)
  const [logo, setLogo] = useState(lightLogo)
  const [agreed, setAgreed] = useState(false)

  const uploadEl = useRef()

  useEffect(() => {
    async function queryUploaded(ecardId) {
      try {
        dispatch(pageLoading())
        const result = await customizedImage(ecardId)
        setUploaded(result.data[result.data.length - 1])
        if (result.data.length > 0) setAgreed(true)
      } catch (e) {
      } finally {
        dispatch(pageLoaded())
        setQueryStatus(false)
      }
    }

    queryUploaded(ecardId)
  }, [])

  const switchLogo = () => {
    const nextIndex = (logos.indexOf(logo) + 1) % logos.length
    setLogo(logos[nextIndex])
  }

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const getUploadData = (dataUrl, id, ossSignature) => {
    const filename = `${ossSignature.dir}${id}/${new Date().getTime()}`
    const formData = new FormData()
    formData.append('key', filename) //存储在oss的文件路径
    formData.append('OSSAccessKeyId', ossSignature.accessid) //accessKeyId
    formData.append('policy', ossSignature.policy) //policy
    formData.append('Signature', ossSignature.signature) //签名
    formData.append('file', dataURLtoBlobOrBlodURL(dataUrl))
    formData.append('success_action_status', 200)
    return formData
  }

  const uploadCroppedImg = useCallback(async () => {
    try {
      dispatch(pageLoading())
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, logo)
      setCroppedImage(croppedImage)

      const { data: ossSignature } = await getOssSignature('ECARD_CUSTOMIZATION')

      const formData = getUploadData(croppedImage, ecardId, ossSignature)
      const uploadedToOss = await uploadSignature({ formData })
      if (uploadedToOss) {
        const submitData = {
          imgUrl: formData.get('key'),
          ecardId,
        }
        if (uploaded && uploaded.id) submitData.id = uploaded.id
        const { data, error } = await getCustomizeEcardUrl(submitData)

        if (error) throw new Error(error)
        if (data && data.id) {
          showToast('卡面上传成功！请等待审核。', 'success', 'center', () => history.goBack())
        }
      } else {
        throw new Error('empty upload response')
      }
    } catch (e) {
      showAlert(formatMessage({ id: 'customize-cert-upload-failed' }), formatMessage({ id: 'common-confirm' }), () =>
        history.goBack()
      )
    } finally {
      dispatch(pageLoaded())
    }
  }, [dispatch, imageSrc, croppedAreaPixels, rotation, logo, ecardId, formatMessage, history])

  const onFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      let imageDataUrl = await readFile(file)

      // apply rotation if needed
      const orientation = await getOrientation(file)
      const rotation = ORIENTATION_TO_ANGLE[orientation]
      if (rotation) {
        imageDataUrl = await getRotatedImage(imageDataUrl, rotation)
      }

      setImageSrc(imageDataUrl)
    }
  }

  const upload = () => {
    if (!agreed) {
      showAlert(formatMessage({ id: 'customize-cert-agree-first' }), formatMessage({ id: 'common-confirm' }), () => {})
    } else {
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
      uploadEl.current.dispatchEvent(clickEvent)
    }
  }

  if (!ecardId) return <Redirect to="/certs" />

  if (queryStatus) return null

  if (uploaded && uploaded.status === 'NEW')
    return (
      <>
        <WingBlank>
          <WhiteSpace />
          <WhiteSpace />
          <CardImageShow cardName={ecardName} path={uploaded.imgUrl} cert={ecard} />
          <WhiteSpace />
          <WhiteSpace />
          <p>
            <FormattedMessage id="customize-cert-upload-success" />
          </p>
          <WhiteSpace />
          <Flex justify="center">
            <Link to="/certs">
              <FormattedMessage id="common-back" />
            </Link>
          </Flex>
        </WingBlank>
      </>
    )

  return (
    <>
      <WhiteSpace />
      <WingBlank>
        <h3>自定义您的电子卡面</h3>
      </WingBlank>
      <WingBlank>
        <div className={styles.upload} ref={cropContainer}>
          <div className={styles.buttons}>
            <input ref={uploadEl} type="file" onChange={onFileChange} accept="image/*" />
          </div>
          <div className={styles.bgImg} onClick={upload}>
            <img
              src={(uploaded && uploaded.imgUrl) || require('../../assets/images/certs/cert_blank.jpg')}
              alt="upload"
            />
          </div>
          {!imageSrc && <UploadButton ecardName={ecardName} upload={upload} />}
          {imageSrc && (
            <>
              <Cropper
                className={styles.cropper}
                image={imageSrc}
                crop={crop}
                cropSize={{
                  width: cropContainer.current.clientWidth,
                  height: cropContainer.current.clientHeight,
                }}
                rotation={rotation}
                zoom={zoom}
                aspect={1524 / 960}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                restrictPosition={false}
                showGrid={false}
                style={{
                  cropAreaStyle: {
                    border: 'none',
                    width: '100% !important',
                    height: 'auto',
                  },
                }}
              />
              <img className={styles.logo} onClick={switchLogo} src={logo} alt="PADI" />
            </>
          )}
        </div>
        <WhiteSpace />
        {imageSrc ? (
          <>
            <Button type="primary" className="btnConfirm" disabled={!imageSrc} onClick={uploadCroppedImg}>
              <FormattedMessage id="customize-cert-crop-btn" />
            </Button>
            <WhiteSpace />
            <Button type="ghost" disabled={!imageSrc} onClick={upload}>
              <FormattedMessage id="customize-cert-change-btn" />
            </Button>
            <WhiteSpace />
            <ul>
              <li>
                <FormattedMessage id="customize-cert-hint6" />
              </li>
              <li>
                <FormattedMessage id="customize-cert-hint7" />
              </li>
            </ul>
          </>
        ) : (
          <>
            <Checkbox.AgreeItem
              checked={agreed}
              onChange={() => setAgreed(!agreed)}
              checkedName={'checkedAgreement'}
              className="small-text"
            >
              <FormattedMessage
                id="customize-cert-hint4"
                values={{
                  link: (
                    <a href="https://assets.padi.com.cn/agreement_sc.html">
                      <FormattedMessage id="common-agreement" />
                    </a>
                  ),
                }}
              />
            </Checkbox.AgreeItem>
            <Button type="primary" disabled={!agreed} onClick={upload}>
              <FormattedMessage id="customize-cert-crop-btn" />
            </Button>
            <WhiteSpace />
            <Hints uploaded={uploaded} />
          </>
        )}

        {testonly && (
          <WingBlank>
            <div className={styles.upload}>
              <div className="upload">
                <img src={croppedImage} alt="" width="100%"></img>
              </div>
            </div>
          </WingBlank>
        )}
      </WingBlank>
    </>
  )
}

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result), false)
    reader.readAsDataURL(file)
  })
}

export default MermaidUpload
